import type {
  BenchSlatPreset,
  SkirtingSpec,
} from './types';

/** The skirting a bench starts with when it is switched on with no boards of its
 *  own: `cavalletto.dxf`'s — 82 × 28 boards, 18 mm apart, 10 mm off the floor. */
export const DEFAULT_SKIRTING: SkirtingSpec = { boards: [82], gaps: [18], thickness: [28], clearance: 10 };

/** A fresh copy of a skirting, so no two benches share the arrays. */
export function cloneSkirting(s: SkirtingSpec): SkirtingSpec {
  return { boards: [...s.boards], gaps: [...s.gaps], thickness: [...s.thickness], clearance: s.clearance };
}

/** The default bench-slat presets seeded into an empty catalogue so the bench
 *  properties dropdown always offers something usable. One gap per slat:
 *  gaps[i] is the gap on the WALL side of slat i. None sets an overlap: a tier
 *  picked from one tucks by the default rule (benches.ts `defaultOverlap`, 56
 *  behind the frame front of the tier above). */
export const DEFAULT_BENCH_PRESETS: BenchSlatPreset[] = [
  { id: 'std-4x90', name: 'Standard 4×90', isDefault: true, slats: [90, 90, 90, 90], gaps: [10, 10, 10, 10] },
  { id: 'comfort-3x120', name: 'Comfort 3×120', slats: [120, 120, 120], gaps: [12, 12, 12] },
  { id: 'mista-var', name: 'Mista doghe+gap variabili', slats: [120, 90, 90, 120], gaps: [10, 10, 15, 10] },
  { id: 'larga-2x140', name: 'Larga 2×140', slats: [140, 140], gaps: [15, 15] },
  // Stenal's bench drawn in `cavalletto.dxf`: six 82×28 boards on a 556 mm frame,
  // the front board — the last board, 28 wide, hanging 110 from the seat top — and
  // the drawing's skirting, ready to switch on.
  {
    id: 'elegant-6x82', name: 'Elegant 6×82', slats: [82, 82, 82, 82, 82, 82, 28], gaps: [0, 15, 15, 15, 15, 4, 0],
    slatThickness: [28, 28, 28, 28, 28, 28, 110], skirtingBoards: cloneSkirting(DEFAULT_SKIRTING),
  },
];

/** The skirting a bench (or preset) builds, or null while it is off. Switched on
 *  with no boards of its own, it is {@link DEFAULT_SKIRTING}. */
export function skirtingOf(b: { skirting?: boolean; skirtingBoards?: SkirtingSpec }): SkirtingSpec | null {
  if (!b.skirting) return null;
  return b.skirtingBoards ?? DEFAULT_SKIRTING;
}

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** A stored skirting read back, or undefined when it is not one: every board a
 *  positive height, a gap ≥ 0 and a positive thickness for each (missing ones
 *  taken from the board before, else 18 / 28), a clearance ≥ 0 (else 10). */
export function normalizeSkirting(v: unknown): SkirtingSpec | undefined {
  const o = v as Partial<Record<keyof SkirtingSpec, unknown>> | null | undefined;
  if (!o || typeof o !== 'object' || !Array.isArray(o.boards)) return undefined;
  const boards = (o.boards as unknown[]).map(Number);
  if (!boards.length || !boards.every((b) => finite(b) && b > 0)) return undefined;
  const list = (raw: unknown, ok: (n: number) => boolean, fallback: number): number[] => {
    const src = Array.isArray(raw) ? (raw as unknown[]).map(Number) : [];
    const out: number[] = [];
    for (let i = 0; i < boards.length; i++) out.push(finite(src[i]) && ok(src[i]) ? src[i] : out[i - 1] ?? fallback);
    return out;
  };
  const clearance = Number(o.clearance);
  return {
    boards,
    gaps: list(o.gaps, (n) => n >= 0, DEFAULT_SKIRTING.gaps[0]),
    thickness: list(o.thickness, (n) => n > 0, DEFAULT_SKIRTING.thickness[0]),
    clearance: finite(clearance) && clearance >= 0 ? clearance : DEFAULT_SKIRTING.clearance,
  };
}

/** The JSON a preset is stored as. */
export function benchPresetBlob(p: BenchSlatPreset): Record<string, unknown> {
  return {
    id: p.id, name: p.name, isDefault: !!p.isDefault, slats: p.slats, gaps: p.gaps, slatThickness: p.slatThickness,
    ...(p.overlap != null ? { overlap: p.overlap } : {}),
    ...(p.skirting ? { skirting: true } : {}),
    ...(p.skirtingBoards ? { skirtingBoards: p.skirtingBoards } : {}),
  };
}

/** A stored preset's JSON read back, or null when it is not one. A preset saved
 *  while the front board was a field of its own (`lip`) gets it as its last
 *  board, and an overlap of its own grows by its thickness so the tier in front
 *  stays put (none: the default rule already reaches past it);
 *  a `closure` is its skirting; switched on with no boards, it takes
 *  {@link DEFAULT_SKIRTING}. */
export function benchPresetFromBlob(parsed: (Partial<BenchSlatPreset> & { closure?: unknown; lip?: unknown }) | undefined, rowId: number): BenchSlatPreset | null {
  if (!parsed || !parsed.id || !Array.isArray(parsed.slats)) return null;
  const on = parsed.skirting === true || parsed.closure === true;
  const boards = normalizeSkirting(parsed.skirtingBoards) ?? (on ? cloneSkirting(DEFAULT_SKIRTING) : undefined);
  const p: BenchSlatPreset = {
    rowId,
    id: String(parsed.id),
    name: String(parsed.name ?? parsed.id),
    isDefault: !!parsed.isDefault,
    slats: parsed.slats.map(Number),
    gaps: Array.isArray(parsed.gaps) ? parsed.gaps.map(Number) : [],
    slatThickness: Array.isArray(parsed.slatThickness) ? parsed.slatThickness.map(Number) : undefined,
    ...(typeof parsed.overlap === 'number' ? { overlap: parsed.overlap } : {}),
    ...(on ? { skirting: true } : {}),
    ...(boards ? { skirtingBoards: boards } : {}),
  };
  const l = parsed.lip as { thickness?: unknown; height?: unknown } | undefined;
  const t = Number(l?.thickness), h = Number(l?.height);
  if (Number.isFinite(t) && Number.isFinite(h) && t > 0 && h > 0) {
    const n = p.slats.length;
    p.gaps = [...Array.from({ length: n }, (_, i) => p.gaps[i] ?? 0), 0];
    p.slatThickness = [...Array.from({ length: n }, (_, i) => p.slatThickness?.[i] ?? 28), h];
    p.slats = [...p.slats, t];
    if (p.overlap != null) p.overlap += t;
  }
  return p;
}

/** The suffixes the generated skirting variants' ids carried (`<id>+skirting`,
 *  and `<id>+closure` before that): a bench naming one now names its preset,
 *  with the skirting on. */
const VARIANT_SUFFIXES = ['+skirting', '+closure'];

/** A bench (or catalogue template) saved under an older skirting model, read
 *  now: `closure` becomes `skirting`, a variant's preset id its preset's (the
 *  skirting on), the old front-board field (`lip`) is dropped — the front board
 *  is the last board —, a skirting switched on with no boards takes
 *  {@link DEFAULT_SKIRTING}, and stored boards that cannot be read are dropped.
 *  Changes the bench in place; true when anything changed. */
export function migrateBenchSkirting(b: { skirting?: boolean; skirtingBoards?: SkirtingSpec; slatPresetId?: string }): boolean {
  const old = b as { closure?: unknown; lip?: unknown; skirting?: boolean; skirtingBoards?: unknown; slatPresetId?: string };
  let changed = false;
  if ('closure' in old) {
    if (old.closure === true) old.skirting = true;
    delete old.closure;
    changed = true;
  }
  if ('lip' in old) { delete old.lip; changed = true; }
  const suffix = VARIANT_SUFFIXES.find((s) => old.slatPresetId?.endsWith(s));
  if (suffix && old.slatPresetId) {
    old.slatPresetId = old.slatPresetId.slice(0, -suffix.length);
    old.skirting = true;
    changed = true;
  }
  if (old.skirtingBoards !== undefined) {
    const read = normalizeSkirting(old.skirtingBoards);
    if (JSON.stringify(read) !== JSON.stringify(old.skirtingBoards)) {
      if (read) old.skirtingBoards = read; else delete old.skirtingBoards;
      changed = true;
    }
  }
  if (old.skirting && !old.skirtingBoards) { old.skirtingBoards = cloneSkirting(DEFAULT_SKIRTING); changed = true; }
  return changed;
}
